export type OrgStatus = 'active' | 'inactive' | 'suspended'

export const ORG_STATUS_OPTIONS: Array<{
  value: OrgStatus | ''
  label: string
  description: string
}> = [
  { value: '', label: 'All statuses', description: '' },
  {
    value: 'active',
    label: 'Active',
    description: 'Storefront and store admin operate normally.',
  },
  {
    value: 'inactive',
    label: 'Inactive (setup)',
    description:
      'Default for new organisations. Not live yet — customer storefront stays unavailable while merchants finish setup. Activate when ready to go live.',
  },
  {
    value: 'suspended',
    label: 'Suspended',
    description:
      'Platform-enforced pause — customer storefront is unavailable (same as inactive/suspended org status on API). Store admin login is not blocked by org status alone; suspend accounts separately if needed.',
  },
]

export function orgStatusLabel(status: string | undefined): string {
  if (status === 'active') return 'Active'
  if (status === 'suspended') return 'Suspended'
  if (status === 'inactive') return 'Inactive (setup)'
  return status || 'Unknown'
}

export function orgStatusDescription(status: string | undefined): string {
  return ORG_STATUS_OPTIONS.find((o) => o.value === status)?.description ?? ''
}

export function orgStatusBadgeClass(status: string | undefined): string {
  const base =
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset'
  if (status === 'active') {
    return `${base} bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 dark:text-emerald-300`
  }
  if (status === 'suspended') {
    return `${base} bg-rose-500/15 text-rose-800 ring-rose-500/25 dark:text-rose-300`
  }
  if (status === 'inactive') {
    return `${base} bg-amber-500/15 text-amber-900 ring-amber-500/25 dark:text-amber-200`
  }
  return `${base} bg-muted text-muted-foreground ring-border`
}

export function OrgStatusBadge({ status }: { status: string | undefined }) {
  return (
    <span className={orgStatusBadgeClass(status)}>{orgStatusLabel(status)}</span>
  )
}

export function isRestrictedOrgStatus(status: string | undefined): boolean {
  return status === 'inactive' || status === 'suspended'
}

export function statusChangeNeedsConfirmation(
  previous: string | undefined,
  next: string | undefined,
): boolean {
  if (!previous || !next || previous === next) return false
  if (next === 'suspended') return true
  if (previous === 'active' && next === 'inactive') return true
  return false
}

export function statusChangeConfirmMessage(next: string | undefined): string {
  if (next === 'suspended') {
    return 'Suspending blocks the customer storefront (public shop returns unavailable). Continue?'
  }
  if (next === 'inactive') {
    return 'Setting inactive hides the customer storefront until you activate the organisation again. Continue?'
  }
  return 'Apply this status change?'
}
